import { useEffect, useState } from "react";
import { Loader2, BarChart3, Download } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

/**
 * Compact admin-sidebar card: each client's latest Business Intelligence
 * Assessment date + coverage score. Click a row to download its branded PDF.
 */
export default function AssessmentsOverviewCard() {
  const [clients, setClients] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => {
    api.get("/expenses/business-intelligence/overview")
      .then(({ data }) => setClients(data.clients || []))
      .catch(() => setClients([]));
  };
  useEffect(() => {
    load();
    const tick = () => { if (document.visibilityState === "visible") load(); };
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, []);

  const download = async (c) => {
    if (!c.id) return;
    setBusy(c.id);
    try {
      const res = await api.get(`/expenses/business-intelligence/${c.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `elevare-bi-${(c.business_name || "client").replace(/\s+/g, "-").toLowerCase()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download that assessment.");
    } finally {
      setBusy(null);
    }
  };

  const fmt = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); } catch { return ""; } };
  const tone = (c) => (c >= 80 ? "text-[#4d5a24]" : c >= 50 ? "text-[#a3823f]" : "text-[#b23a48]");

  if (clients === null) {
    return (
      <div className="hidden md:flex rounded-xl border border-[#1e3d3a]/12 bg-white/80 backdrop-blur-sm px-3 py-4 items-center gap-2 text-[#1e3d3a]/50 text-xs">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Assessments…
      </div>
    );
  }
  if (!clients.length) return null;

  return (
    <div data-testid="assessments-overview-card" className="hidden md:block rounded-xl border border-[#1e3d3a]/12 bg-white/80 backdrop-blur-sm px-3 py-3">
      <p className="text-[10px] uppercase tracking-[0.24em] text-[#1e3d3a]/55 mb-2 px-1 flex items-center gap-1.5">
        <BarChart3 className="h-3 w-3 text-[#c9a961]" /> Client assessments
      </p>
      <ul className="divide-y divide-[#1e3d3a]/6 max-h-64 overflow-y-auto">
        {clients.map((c, i) => (
          <li key={c.id || i}>
            <button
              data-testid={`assessment-row-${i}`}
              onClick={() => download(c)}
              disabled={busy === c.id}
              title="Download assessment PDF"
              className="w-full flex items-center gap-2 py-2 px-1 text-left rounded-lg hover:bg-[#c9a961]/10 transition-colors duration-200 disabled:opacity-60 group"
            >
              <div className="min-w-0 flex-1">
                <p className="text-[11px] text-[#1e3d3a] truncate font-medium">{c.business_name}</p>
                <p className="text-[9.5px] uppercase tracking-[0.14em] text-[#1e3d3a]/45">{fmt(c.created_at)}</p>
              </div>
              <div className="text-right shrink-0">
                <span className={`font-head text-base font-light tabular-nums leading-none ${tone(c.coverage)}`}>{c.coverage}%</span>
                <p className="text-[9px] text-[#1e3d3a]/40 tabular-nums">{c.analyzed}/{c.total}</p>
              </div>
              {busy === c.id
                ? <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-[#1e3d3a]/50" />
                : <Download className="h-3.5 w-3.5 shrink-0 text-[#1e3d3a]/25 group-hover:text-[#c9a961] transition-colors duration-200" />}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
